import { Form, Formik, useField } from 'formik';
import { Lock } from 'lucide-react';
import { useRouter } from '@uirouter/react';
import * as Yup from 'yup';

import { notifySuccess } from '@/portainer/services/notifications';
import { useUpdateUserMutation } from '@/portainer/users/queries/useUpdateUserMutation';
import { User } from '@/portainer/users/types';

import { Widget } from '@@/Widget';
import { FormControl } from '@@/form-components/FormControl';
import { FormActions } from '@@/form-components/FormActions';
import { Input } from '@@/form-components/Input';
import { PasswordCheckHint } from '@@/PasswordCheckHint';
import { confirmChangePassword } from '@@/modals/confirm';

import { useChangeOwnPasswordMutation } from './queries/useChangeOwnPasswordMutation';

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const initialValues: FormValues = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

export function PasswordForm({
  user,
  isSelf,
  requiredPasswordLength,
}: {
  user: User;
  isSelf: boolean;
  requiredPasswordLength: number;
}) {
  const router = useRouter();
  const resetMutation = useUpdateUserMutation();
  const changeOwnMutation = useChangeOwnPasswordMutation(user.Id);
  const isLoading = resetMutation.isLoading || changeOwnMutation.isLoading;

  const validation = Yup.object({
    currentPassword: isSelf
      ? Yup.string().required('Your current password is required')
      : Yup.string(),
    newPassword: Yup.string()
      .required('A new password is required')
      .min(
        requiredPasswordLength,
        `The password must be at least ${requiredPasswordLength} characters long.`
      ),
    confirmPassword: Yup.string()
      .required('Please confirm the new password')
      .oneOf([Yup.ref('newPassword')], 'The passwords must match'),
  });

  return (
    <Widget>
      <Widget.Title icon={Lock} title="Change user password" />
      <Widget.Body>
        <Formik<FormValues>
          initialValues={initialValues}
          validationSchema={validation}
          onSubmit={handleSubmit}
        >
          {({ values, isValid }) => (
            <Form className="form-horizontal">
              {isSelf && (
                <PasswordField
                  name="currentPassword"
                  label="Current password"
                  inputId="user-current-password"
                />
              )}
              <PasswordField
                name="newPassword"
                label="New password"
                inputId="user-new-password"
              />
              <PasswordField
                name="confirmPassword"
                label="Confirm password"
                inputId="user-confirm-password"
              />

              <div className="form-group">
                <div className="col-sm-12">
                  <PasswordCheckHint
                    passwordValid={
                      values.newPassword.length >= requiredPasswordLength
                    }
                  />
                </div>
              </div>

              <FormActions
                submitLabel="Update password"
                loadingText="Updating"
                isLoading={isLoading}
                isValid={isValid && !!values.newPassword}
                data-cy="user-update-password-button"
              />
            </Form>
          )}
        </Formik>
      </Widget.Body>
    </Widget>
  );

  async function handleSubmit(values: FormValues) {
    if (isSelf) {
      await changeOwnPassword(values);
      return;
    }

    resetMutation.mutate(
      { userId: user.Id, payload: { newPassword: values.newPassword } },
      {
        onSuccess() {
          notifySuccess('Success', 'Password successfully updated');
          router.stateService.reload();
        },
      }
    );
  }

  async function changeOwnPassword(values: FormValues) {
    if (!(await confirmChangePassword())) {
      return;
    }

    changeOwnMutation.mutate(
      {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      },
      {
        onSuccess() {
          notifySuccess('Success', 'Password successfully updated');
          router.stateService.go('portainer.logout');
        },
      }
    );
  }
}

function PasswordField({
  name,
  label,
  inputId,
}: {
  name: string;
  label: string;
  inputId: string;
}) {
  const [{ onBlur, onChange, value }, { error }] = useField<string>(name);

  return (
    <FormControl inputId={inputId} label={label} required errors={error}>
      <Input
        type="password"
        id={inputId}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        autoComplete="new-password"
        data-cy={`${inputId}-input`}
      />
    </FormControl>
  );
}
